import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Rss, Download as DownloadIcon, Copy, Check, FileText, Terminal, Server, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface LicenseStatus {
  customerEmail: string;
  status: string;
  isActivated: boolean;
  instanceUrl: string | null;
  activatedAt: string | null;
  lastSeenAt: string | null;
  purchasedAt: string;
}

export default function Download() {
  const { key } = useParams<{ key: string }>();
  const [loading, setLoading] = useState(true);
  const [license, setLicense] = useState<LicenseStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function checkLicense() {
      if (!key) {
        setError('No license key provided');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/license/status/${key}`);
        const data = await response.json();

        if (response.ok) {
          setLicense(data);
        } else {
          setError(data.error || 'Invalid license key');
        }
      } catch (err) {
        setError('Failed to verify license');
      } finally {
        setLoading(false);
      }
    }

    checkLicense();
  }, [key]);

  const copyToClipboard = () => {
    if (key) {
      navigator.clipboard.writeText(key);
      setCopied(true);
      toast.success('License key copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-accent-600 animate-spin mx-auto mb-4" />
          <p className="text-ink-muted">Verifying license...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-canvas rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">!</span>
          </div>
          <h1 className="text-2xl font-bold text-ink mb-2">Invalid License</h1>
          <p className="text-ink-muted mb-6">{error}</p>
          <a
            href="/buy"
            className="inline-block px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-semibold rounded-lg transition-colors"
          >
            Purchase a License
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-canvas border-b border-edge">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent-600 rounded-lg flex items-center justify-center">
              <Rss className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-ink">OurSS Download</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* License Info */}
        <div className="bg-canvas rounded-2xl shadow-lg border border-edge overflow-hidden mb-8">
          <div className="p-6 bg-gray-50 border-b border-edge flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-ink">Your License</h2>
              <p className="text-sm text-ink-muted">{license?.customerEmail}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              license?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {license?.status}
            </span>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3">
              <code className="flex-1 px-4 py-3 bg-gray-100 rounded-lg font-mono text-lg text-ink">
                {key}
              </code>
              <button
                onClick={copyToClipboard}
                className="p-3 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copy to clipboard"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5 text-ink-muted" />
                )}
              </button>
            </div>
            {license?.isActivated && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Currently activated on:</strong> {license.instanceUrl || 'Unknown'}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  Last seen: {license.lastSeenAt ? new Date(license.lastSeenAt).toLocaleString() : 'Never'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Download Options */}
        <h2 className="text-2xl font-bold text-ink mb-6">Download OurSS</h2>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Docker Download */}
          <div className="bg-canvas rounded-xl border border-edge p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Server className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">Docker</h3>
                <p className="text-sm text-ink-muted">Recommended</p>
              </div>
            </div>
            <p className="text-ink-muted text-sm mb-4">
              The easiest way to deploy OurSS. Works on any server with Docker.
            </p>
            <div className="bg-gray-900 rounded-lg p-4 mb-4">
              <code className="text-green-400 text-sm font-mono break-all">
                docker pull ghcr.io/poplicola/ourss:latest
              </code>
            </div>
            <a
              href="/docs/docker"
              className="text-accent-600 hover:text-accent-700 text-sm font-medium"
            >
              View Docker setup guide →
            </a>
          </div>

          {/* Manual Download */}
          <div className="bg-canvas rounded-xl border border-edge p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Terminal className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">Manual Install</h3>
                <p className="text-sm text-ink-muted">Node.js 20+</p>
              </div>
            </div>
            <p className="text-ink-muted text-sm mb-4">
              Download and extract on your server. Requires Node.js 20 or later.
            </p>
            <a
              href={`/api/download/${key}/ourss.tar.gz`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg transition-colors"
            >
              <DownloadIcon className="w-4 h-4" />
              Download v1.0.0
            </a>
          </div>
        </div>

        {/* Quick Start */}
        <div className="bg-canvas rounded-2xl shadow-lg border border-edge overflow-hidden mb-8">
          <div className="p-6 bg-gray-50 border-b border-edge">
            <h2 className="text-lg font-semibold text-ink">Quick Start</h2>
          </div>
          <div className="p-6">
            <ol className="space-y-6">
              <li>
                <h3 className="font-medium text-ink mb-2">1. Download and extract</h3>
                <div className="bg-gray-900 rounded-lg p-4">
                  <code className="text-green-400 text-sm font-mono">
                    tar -xzf ourss.tar.gz && cd ourss
                  </code>
                </div>
              </li>
              <li>
                <h3 className="font-medium text-ink mb-2">2. Run the installer</h3>
                <div className="bg-gray-900 rounded-lg p-4">
                  <code className="text-green-400 text-sm font-mono">
                    ./install.sh
                  </code>
                </div>
              </li>
              <li>
                <h3 className="font-medium text-ink mb-2">3. Or use Docker Compose</h3>
                <div className="bg-gray-900 rounded-lg p-4 space-y-1">
                  <code className="text-green-400 text-sm font-mono block">
                    # Edit .env with your LICENSE_KEY
                  </code>
                  <code className="text-green-400 text-sm font-mono block">
                    docker-compose up -d
                  </code>
                </div>
              </li>
            </ol>
          </div>
        </div>

        {/* Documentation Links */}
        <div className="grid md:grid-cols-3 gap-4">
          <a
            href="/docs/installation"
            className="flex items-center gap-3 p-4 bg-canvas rounded-xl border border-edge hover:border-accent-300 transition-colors"
          >
            <FileText className="w-5 h-5 text-gray-400" />
            <span className="font-medium text-ink">Installation Guide</span>
          </a>
          <a
            href="/docs/configuration"
            className="flex items-center gap-3 p-4 bg-canvas rounded-xl border border-edge hover:border-accent-300 transition-colors"
          >
            <FileText className="w-5 h-5 text-gray-400" />
            <span className="font-medium text-ink">Configuration</span>
          </a>
          <a
            href="/docs/updating"
            className="flex items-center gap-3 p-4 bg-canvas rounded-xl border border-edge hover:border-accent-300 transition-colors"
          >
            <FileText className="w-5 h-5 text-gray-400" />
            <span className="font-medium text-ink">Updating OurSS</span>
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 bg-canvas border-t border-edge mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-ink-muted">
          <p>Need help? Email support@yourss.fourfold.co</p>
        </div>
      </footer>
    </div>
  );
}
